var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createParser } from 'eventsource-parser';
import { ApiUrl, AuthorizationHeaders } from 'api/environment';
export class HelpAgentError extends Error {
    constructor(code) {
        super(code);
        this.code = code;
        this.name = 'HelpAgentError';
    }
}
function checkResponse(response) {
    return __awaiter(this, void 0, void 0, function* () {
        if (response.ok)
            return;
        if (response.status === 401 || response.status === 403)
            throw new HelpAgentError('UNAUTHORIZED');
        const body = yield response.json().catch(() => null);
        throw new HelpAgentError((body === null || body === void 0 ? void 0 : body.code) || 'UNAVAILABLE');
    });
}
export function agentAvailable(signal) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(`${ApiUrl}/help-agent/status`, {
            headers: AuthorizationHeaders(),
            cache: 'no-store',
            signal,
        });
        yield checkResponse(response);
        const body = yield response.json();
        return body.available === true;
    });
}
// Keep whole user/assistant pairs, with room for the next question.
export function boundedHistory(messages) {
    const history = messages.slice(-21);
    while (history.length > 1 &&
        history.reduce((sum, message) => sum + message.content.length, 0) > 24000) {
        history.splice(0, 2);
    }
    return history;
}
export function streamAgent(language, messages, signal, onEvent) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const response = yield fetch(`${ApiUrl}/help-agent/chat`, {
            method: 'POST',
            headers: Object.assign(Object.assign({}, AuthorizationHeaders()), { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }),
            body: JSON.stringify({ language, messages: boundedHistory(messages) }),
            signal,
        });
        yield checkResponse(response);
        if (!response.body || !((_a = response.headers.get('content-type')) === null || _a === void 0 ? void 0 : _a.includes('text/event-stream'))) {
            throw new HelpAgentError('UNAVAILABLE');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let completed = false;
        let length = 0;
        const parser = createParser({
            maxBufferSize: 131072,
            onError: () => {
                throw new HelpAgentError('INCOMPLETE_RESPONSE');
            },
            onEvent: ({ data }) => {
                if (completed)
                    return;
                const event = JSON.parse(data);
                if (event.type === 'error')
                    throw new HelpAgentError(event.code || 'UNAVAILABLE');
                if (event.type === 'delta') {
                    if (typeof event.content !== 'string')
                        throw new HelpAgentError('INCOMPLETE_RESPONSE');
                    length += event.content.length;
                    if (length > 12000)
                        throw new HelpAgentError('INCOMPLETE_RESPONSE');
                    onEvent({ type: 'delta', content: event.content });
                }
                else if (event.type === 'sources') {
                    if (!Array.isArray(event.sources) || event.sources.length > 64) {
                        throw new HelpAgentError('INCOMPLETE_RESPONSE');
                    }
                    const sources = event.sources.filter((source) => typeof (source === null || source === void 0 ? void 0 : source.title) === 'string' &&
                        typeof (source === null || source === void 0 ? void 0 : source.url) === 'string' &&
                        /^\/help\/article\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(source.url));
                    onEvent({ type: 'sources', sources });
                }
                else if (event.type === 'done') {
                    if (!length)
                        throw new HelpAgentError('INCOMPLETE_RESPONSE');
                    completed = true;
                    onEvent({ type: 'done' });
                }
            },
        });
        try {
            while (!completed) {
                // A network chunk may end inside a UTF-8 character or an SSE event.
                // eslint-disable-next-line no-await-in-loop
                const { value, done } = yield reader.read();
                if (done)
                    break;
                parser.feed(decoder.decode(value, { stream: true }));
            }
            if (!completed)
                throw new HelpAgentError('INCOMPLETE_RESPONSE');
        }
        finally {
            yield reader.cancel().catch(() => undefined);
            reader.releaseLock();
        }
    });
}
